/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, useMemo } from 'react';
import { useRouteLoaderData } from 'react-router';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/spinner';
import { XIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UITarget } from '@/targets/ui-target';
import { useTranslation } from 'react-i18next';
import { TrackingConsent } from '@/types/tracking-consent';

type ProcessingAction = 'accept' | 'decline' | 'close' | null;

export interface TrackingConsentBannerProps {
    /**
     * Optional callback function called after user responds to consent banner.
     * Called after SLAS token is refreshed to ensure hybrid compatibility.
     * Can return a Promise for async operations.
     *
     * @param consent - TrackingConsent enum value (Accepted or Declined)
     *
     * @example
     * <TrackingConsentBanner
     *   onConsentChange={(consent) => {
     *     if (window.customAnalytics) {
     *       window.customAnalytics.setTrackingEnabled(consent === TrackingConsent.Accepted);
     *     }
     *   }}
     * />
     *
     * @example
     * // Async callback
     * <TrackingConsentBanner
     *   onConsentChange={async (consent) => {
     *     await sendConsentToServer(consent);
     *   }}
     * />
     */
    onConsentChange?: (consent: TrackingConsent) => void | Promise<void>;
}

/**
 * Tracking Consent Banner Component
 *
 * Displays a non-intrusive banner at the bottom of the page to collect user consent
 * for tracking. The banner:
 * - Only shows if tracking consent is enabled in config and user hasn't responded
 * - Refreshes SLAS token with tracking consent value (server sets dw_dnt cookie via Set-Cookie header)
 * - Supports custom onConsentChange callback for external analytics integration
 *
 * @example
 * // Basic usage (default behavior)
 * <TrackingConsentBanner />
 *
 * @example
 * // With custom analytics integration
 * <TrackingConsentBanner
 *   onConsentChange={(consent) => {
 *     configureExternalAnalytics(consent === TrackingConsent.Accepted);
 *   }}
 * />
 */
export function TrackingConsentBanner({ onConsentChange }: TrackingConsentBannerProps) {
    const { shouldShowBanner, setTrackingConsent, defaultTrackingConsent, isTrackingConsentEnabled } =
        useTrackingConsent();
    const [processingAction, setProcessingAction] = useState<ProcessingAction>(null);
    const config = useConfig();
    const { t } = useTranslation('trackingConsent');

    const trackingConsentConfig = useMemo(
        () => config.engagement?.analytics?.trackingConsent,
        [config.engagement?.analytics?.trackingConsent]
    );

    const isProcessing = processingAction !== null;

    const handleConsent = async (consent: TrackingConsent, action: ProcessingAction) => {
        if (isProcessing || !isTrackingConsentEnabled) return;

        setProcessingAction(action);

        try {
            await setTrackingConsent(consent);

            if (onConsentChange) {
                await onConsentChange(consent);
            }
            // eslint-disable-next-line no-empty
        } catch {
        } finally {
            setProcessingAction(null);
        }
    };

    const handleClose = () => {
        void handleConsent(defaultTrackingConsent, 'close');
    };

    const handleAccept = () => {
        void handleConsent(TrackingConsent.Accepted, 'accept');
    };

    const handleDecline = () => {
        void handleConsent(TrackingConsent.Declined, 'decline');
    };

    const rootData = useRouteLoaderData('root');

    if (!shouldShowBanner || rootData?.pageDesignerMode) {
        return null;
    }

    const position = trackingConsentConfig?.position ?? 'bottom-center';

    /**
     * Horizontal placement, expressed as MARGINS rather than insets.
     *
     * The banner is `position: sticky` (see the block above the wrapper), so it
     * is a normal flex item of `<body class="flex flex-col min-h-screen">` and
     * `left-*` / `right-*` would only take effect on horizontal scroll. `auto`
     * margins are what position a flex item on its cross axis, and they leave
     * the reserved flow height untouched.
     *
     * Below `md` the banner is `w-full` on every arm, so all three collapse to
     * the same full-bleed row — matching the previous `left-0` / `right-0`
     * behaviour, which was equally width-bound.
     */
    const positionClasses = {
        'bottom-left': 'md:mr-auto md:ml-4',
        'bottom-right': 'md:ml-auto md:mr-4',
        'bottom-center': 'md:mx-auto',
    };

    return (
        <UITarget targetId="sfcc.global.cookies.banner">
            {/*
             * STICKY, NOT FIXED — and the difference is the whole point.
             *
             * WHAT THE OVERLAY BREAKS. A `position: fixed` banner is out of
             * flow, so the document has no accommodation for it and the last
             * bandful of every page stays covered — at MAXIMUM SCROLL that
             * content cannot be scrolled clear at all. Measured on the running
             * dev server at a clientWidth of 390, guarded on `location.href` +
             * `document.documentElement.clientWidth` + `data-brand` inline: at
             * max scroll `document.elementFromPoint` at their own centres
             * returned the banner instead of NM's `Site Terms and Policy` and
             * BG's `Privacy Policy` and `Terms of Use` footer links. A consent
             * notice that buries the privacy link is the wrong end of the
             * trade, and that reservation must not regress.
             *
             * It is NOT a `pointer-events` problem, and that was measured
             * rather than assumed: this wrapper and its Card are coincident to
             * the pixel — dead space 0 on all four sides at 390 and at 1280 —
             * and the Card is fully opaque, so there is no transparent region
             * for clicks to pass through. Nor is it a z-index problem: the
             * content underneath needs to be REACHABLE, not stacked above a
             * banner the shopper is supposed to read. What is missing is flow
             * space.
             *
             * WHY THIS SHAPE. The first fix supplied that flow space with a
             * sibling `<div style={{ height: measuredPx }} />`, measured off
             * the banner in a `useEffect`. It reserved the right amount at the
             * wrong TIME: server-rendered at 0 and re-rendered at full height
             * once hydration ran, i.e. a layout shift by construction on every
             * page. Sampled every 25ms from navigation on the dev server, both
             * brands, guards asserted inline — NM cart 1280 `0px -> 272px` at
             * ~330ms (twice, identical), BG cart 1280 `0px -> 292px` at 348ms,
             * NM cart 390 `0px -> 276px` at 314ms. The worst RELATIVE effect
             * lands on the shortest page, which is the cart.
             *
             * `sticky` removes the measurement instead of re-timing it. A
             * sticky box is a normal flex item of `<body class="flex flex-col
             * min-h-screen">`, so it reserves EXACTLY its own height, at first
             * paint, server and client alike — correctness becomes a property
             * of the mechanism rather than of a number someone measured. A
             * hardcoded `min-height` would have been the original defect again
             * with the shift removed: right until the copy, the locale or a
             * brand's CTA wraps to another line, and then silently short.
             *
             * It pins like `fixed` because of the layout above it, not by
             * luck: `_app.tsx` renders header / `<main class="grow">` /
             * `<Footer />` as direct flex children of that same body, so the
             * content preceding this element is always at least `100vh` and
             * this element's own flow position is therefore always at or below
             * the viewport bottom — which is precisely the condition under
             * which `bottom-0` sticks. At maximum scroll it releases into its
             * natural position at the document end, which is the behaviour the
             * reservation exists to produce.
             */}
            <div
                className={cn(
                    'sticky z-50 shrink-0 w-full md:max-w-md bottom-0 md:bottom-4 md:mb-4',
                    'animate-in slide-in-from-bottom-5 duration-300',
                    positionClasses[position] || positionClasses['bottom-center']
                )}
                role="dialog"
                aria-labelledby="tracking-consent-banner-title"
                aria-describedby="tracking-consent-banner-description">
                <Card className="relative shadow-lg">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-4 top-4 h-8 w-8 shrink-0 opacity-70 transition-opacity hover:opacity-100"
                        onClick={handleClose}
                        disabled={isProcessing}
                        aria-label={t('closeAriaLabel')}>
                        {processingAction === 'close' ? <Spinner size="sm" /> : <XIcon className="size-4" />}
                        <span className="sr-only">{t('closeAriaLabel')}</span>
                    </Button>
                    <CardContent className="pt-6 pr-10">
                        <div className="space-y-2">
                            <h2 id="tracking-consent-banner-title" className="text-sm font-semibold">
                                {t('title')}
                            </h2>
                            <p id="tracking-consent-banner-description" className="text-sm text-muted-foreground">
                                {t('description')}
                            </p>
                        </div>
                    </CardContent>
                    <CardFooter className="flex gap-2 pt-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDecline}
                            disabled={isProcessing}
                            className="flex-1">
                            {processingAction === 'decline' && <Spinner size="sm" />}
                            {t('decline')}
                        </Button>
                        <Button
                            variant="default"
                            size="sm"
                            onClick={handleAccept}
                            disabled={isProcessing}
                            className="flex-1">
                            {processingAction === 'accept' && <Spinner size="sm" />}
                            {t('accept')}
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        </UITarget>
    );
}
